"use client";

import { useState, useEffect, useContext } from "react";
import {
  Box,
  TextField,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Button,
  Typography,
  InputAdornment,
  Chip,
  Tooltip,
  Card,
  CardContent,
  Divider,
  useTheme,
  alpha,
  CircularProgress,
  Alert,
  Fade,
  Autocomplete,
  Paper,
  Collapse,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import BadgeIcon from "@mui/icons-material/Badge";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { ErpContext } from "../../context/ErpContext.js";
import ModalCrearActualizarEntidad from "./ModalCrearActualizarEntidad";
import CustomPagination from "../transaccionComponent/CustomPagination";

const TablaEntidadProveedor = () => {
  const theme = useTheme();
  const { 
    getEntidadFilterTipoEntidad,
    isNavbarCollapsed,
    entidadAtributoNoEliminados,
    getEntidadAtributoNoEliminados,
    getEntidadAtributoClasificacionByIdAtributo,
  } = useContext(ErpContext);

  // Estados
  const [Clientes, setProveedores] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [selectedProveedor, setSelectedProveedor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Paginación del servidor
  const [page, setPage] = useState(1);
  const [limit] = useState(100);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Estados para filtros de atributos
  const [opcionesPorAtributo, setOpcionesPorAtributo] = useState({});
  const [atributosEntidadFiltro, setAtributosEntidadFiltro] = useState({});
  const [mostrarFiltrosAtributos, setMostrarFiltrosAtributos] = useState(false);

  // Cargar atributos y opciones al montar
  useEffect(() => {
    const cargarDatos = async () => {
      if (!entidadAtributoNoEliminados || entidadAtributoNoEliminados.length === 0) {
        await getEntidadAtributoNoEliminados();
      }
      await cargarOpcionesAtributos();
    };
    cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cargar opciones de atributos
  const cargarOpcionesAtributos = async () => {
    if (!entidadAtributoNoEliminados || entidadAtributoNoEliminados.length === 0) return;
    
    const opcionesMap = {};
    for (const atributo of entidadAtributoNoEliminados) {
      try {
        const opciones = await getEntidadAtributoClasificacionByIdAtributo(atributo.id);
        opcionesMap[atributo.id] = opciones || [];
      } catch (error) {
        console.error(`Error al cargar opciones para atributo ${atributo.id}:`, error);
        opcionesMap[atributo.id] = [];
      }
    }
    setOpcionesPorAtributo(opcionesMap);
  };

  // Obtener Clientes con paginación y filtros
  useEffect(() => {
    fetchClientes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchText, atributosEntidadFiltro]);

  const fetchClientes = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getEntidadFilterTipoEntidad(
        1, // idTipoEntidad = 1 para Clientes
        page,
        limit,
        searchText,
        atributosEntidadFiltro
      );
      
      if (result.data) {
        // Formato con paginación del servidor
        setProveedores(result.data || []);
        setTotalPages(result.totalPages || 1);
        setTotalCount(result.total || 0);
      } else {
        // Formato sin paginación (retrocompatibilidad)
        setProveedores(result || []);
        setTotalPages(1);
        setTotalCount(result?.length || 0);
      }
    } catch (err) {
      console.error("Error al cargar Clientes:", err);
      setError("No se pudieron cargar los Clientes. Intente nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  // Abrir modal para EDITAR cliente
  const handleOpenEditModal = (cliente) => {
    setSelectedProveedor(cliente); // Cargar datos en el modal
    setOpenModal(true);
  };

  // Abrir modal para CREAR cliente
  const handleOpenCreateModal = () => {
    setSelectedProveedor({
      id: null,
      descripcion: "",
      apellido: "",
      dniCuitCuil: "",
      telefono: "",
      email: "",
      direccion: "",
      localidad: "",
      provincia: "",
      idTipoEntidad: 2, // Tipo entidad = Cliente
    });
    setOpenModal(true);
  };

  // Cerrar modal y actualizar lista si se guardaron cambios
  const handleCloseModal = async (refresh = false) => {
    setOpenModal(false);
    if (refresh) {
      await fetchClientes();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    }
  };

  // Manejar cambio de página
  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  // Resetear filtros
  const resetFilters = () => {
    setSearchText("");
    setAtributosEntidadFiltro({});
    setPage(1);
  };

  const tableStyles = {
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#888",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: "#555",
    },
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, overflow: "hidden" }}>
      {/* Encabezado */}
      <Card
        elevation={2}
        sx={{
          mb: 3,
          borderRadius: 2,
          overflow: "visible",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <BusinessIcon
              sx={{
                fontSize: 28,
                color: "rgb(244, 147, 14)",
                mr: 1.5,
              }}
            />
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
              Gestión de Clientes
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              alignItems: { xs: "stretch", sm: "center" },
              justifyContent: "space-between",
            }}
          >
            {/* Buscador */}
            <TextField
              placeholder="Buscar por nombre, apellido, CUIT o email..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              sx={{
                maxWidth: { sm: "60%", md: "40%" },
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            {/* Botones de acción */}
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<FilterListIcon />}
                onClick={() => setMostrarFiltrosAtributos(!mostrarFiltrosAtributos)}
                disabled={loading}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  minWidth: 120,
                  borderColor: mostrarFiltrosAtributos || Object.values(atributosEntidadFiltro).some(v => v) 
                    ? "rgb(244, 147, 14)" 
                    : undefined,
                  color: mostrarFiltrosAtributos || Object.values(atributosEntidadFiltro).some(v => v) 
                    ? "rgb(244, 147, 14)" 
                    : undefined,
                }}
              >
                Filtros
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={fetchClientes}
                disabled={loading}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  minWidth: 100,
                }}
              >
                Actualizar
              </Button>

              <Button
                variant="contained"
                startIcon={<AddCircleOutlineIcon />}
                onClick={handleOpenCreateModal}
                disabled={loading}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  boxShadow: 2,
                  minWidth: 140,
                  backgroundColor: "#5b5b5f",
                  "&:hover": {
                    backgroundColor: alpha("#5b5b5f", 0.8),
                  },
                }}
              >
                Nuevo Cliente
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Filtros de Atributos */}
      <Collapse in={mostrarFiltrosAtributos}>
        <Card
          elevation={2}
          sx={{
            mb: 3,
            borderRadius: 2,
            overflow: "visible",
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: "#5b5b5f" }}>
                Filtros por Atributos
              </Typography>
              <Button
                size="small"
                onClick={resetFilters}
                sx={{ textTransform: "none" }}
              >
                Limpiar Filtros
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {entidadAtributoNoEliminados && entidadAtributoNoEliminados.length > 0 ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(3, 1fr)", lg: "repeat(4, 1fr)" },
                  gap: 2,
                }}
              >
                {entidadAtributoNoEliminados.map((atributo, index) => {
                  const atributoNumero = index + 1;
                  const opciones = opcionesPorAtributo[atributo.id] || [];
                  
                  return (
                    <Autocomplete
                      key={atributo.id}
                      size="small"
                      disablePortal
                      options={opciones}
                      getOptionLabel={(option) => option.descripcion || ""}
                      value={opciones.find(o => o.id === atributosEntidadFiltro[atributoNumero]) || null}
                      onChange={(event, newValue) => {
                        setAtributosEntidadFiltro(prev => {
                          const newAtributos = { ...prev };
                          if (newValue) {
                            newAtributos[atributoNumero] = newValue.id;
                          } else {
                            delete newAtributos[atributoNumero];
                          }
                          return newAtributos;
                        });
                        setPage(1); // Resetear a primera página
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={atributo.descripcion}
                          variant="outlined"
                          size="small"
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <BadgeIcon fontSize="small" color="action" />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  );
                })}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
                No hay atributos de entidad configurados.
              </Typography>
            )}
          </CardContent>
        </Card>
      </Collapse>

      {/* Alerta de éxito */}
      <Fade in={success}>
        <Alert
          severity="success"
          sx={{
            mb: 2,
            borderRadius: 2,
            boxShadow: 1,
          }}
          onClose={() => setSuccess(false)}
        >
          Operación completada con éxito
        </Alert>
      </Fade>

      {/* Alerta de error */}
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            borderRadius: 2,
            boxShadow: 1,
          }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Tabla de Clientes */}
      <Card
        elevation={2}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          height: "auto",
        }}
      >
        <Box sx={{ position: "relative" }}>
          {/* Overlay de carga */}
          {loading && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                zIndex: 10,
              }}
            >
              <CircularProgress />
            </Box>
          )}

          <TableContainer
            sx={{
              maxHeight: "calc(100vh - 300px)",
              overflowY: "auto",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#888",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#555",
              },
            }}
          >
            <Table 
              stickyHeader 
              sx={{ 
                tableLayout: "auto",
                minWidth: 1200,
                width: "max-content",
                transition: "all 0.3s ease",
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.813rem",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      minWidth: 80,
                      padding: "8px 12px",
                      whiteSpace: "nowrap",
                      height: "40px",
                    }}
                  >
                    ID
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.813rem",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      minWidth: 180,
                      padding: "8px 12px",
                      height: "40px",
                    }}
                  >
                    Nombre
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.813rem",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      minWidth: 150,
                      padding: "8px 12px",
                      height: "40px",
                    }}
                  >
                    Apellido
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.813rem",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      minWidth: 140,
                      padding: "8px 12px",
                      height: "40px",
                    }}
                  >
                    DNI / CUIT / CUIL
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.813rem",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      minWidth: 220,
                      padding: "8px 12px",
                      height: "40px",
                    }}
                  >
                    Contacto
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.813rem",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      minWidth: 180,
                      padding: "8px 12px",
                      height: "40px",
                    }}
                  >
                    Ubicación
                  </TableCell>
                  {/* Columnas dinámicas de atributos */}
                  {entidadAtributoNoEliminados && entidadAtributoNoEliminados
                    .filter((attr) => !attr.eliminado)
                    .map((attr) => (
                      <TableCell
                        key={attr.id}
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.813rem",
                          backgroundColor: "#5b5b5f",
                          color: "white",
                          minWidth: 150,
                          padding: "8px 12px",
                          height: "40px",
                        }}
                      >
                        {attr.descripcion}
                      </TableCell>
                    ))}
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.813rem",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      minWidth: 100,
                      padding: "8px 12px",
                      height: "40px",
                    }}
                  >
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Clientes.length > 0 ? (
                  Clientes.map((prov) => (
                    <TableRow
                      key={prov.id}
                      hover
                      sx={{
                        "&:nth-of-type(even)": {
                          backgroundColor: "#f8f9fa",
                        },
                        "&:hover": {
                          backgroundColor: "#e3f2fd",
                          transition: "background-color 0.2s ease",
                        },
                        transition: "background-color 0.2s",
                        height: "48px",
                      }}
                    >
                      <TableCell
                        sx={{
                          padding: "6px 12px",
                          borderBottom: "1px solid #e0e0e0",
                        }}
                      >
                        <Chip
                          label={prov.id}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            minWidth: 40,
                            height: 24,
                            justifyContent: "center",
                            borderColor: "#5b5b5f",
                            color: "#5b5b5f",
                          }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "6px 12px",
                          borderBottom: "1px solid #e0e0e0",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <PersonIcon 
                            fontSize="small" 
                            sx={{ 
                              color: "#5b5b5f", 
                              fontSize: "1rem",
                              flexShrink: 0,
                            }} 
                          />
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 500,
                              fontSize: "0.875rem",
                              color: "#212121",
                            }}
                          >
                            {prov.descripcion || "—"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "6px 12px",
                          borderBottom: "1px solid #e0e0e0",
                        }}
                      >
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontSize: "0.875rem",
                            color: "#424242",
                          }}
                        >
                          {prov.apellido || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "6px 12px",
                          borderBottom: "1px solid #e0e0e0",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <BadgeIcon 
                            fontSize="small" 
                            sx={{ 
                              color: "#5b5b5f", 
                              fontSize: "1.1rem",
                              flexShrink: 0,
                            }} 
                          />
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontSize: "0.938rem",
                              color: "#424242",
                              fontFamily: "monospace",
                            }}
                          >
                            {prov.dniCuitCuil || "—"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "6px 12px",
                          borderBottom: "1px solid #e0e0e0",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          {prov.email && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <EmailIcon 
                                fontSize="small" 
                                sx={{ 
                                  color: "#5b5b5f", 
                                  fontSize: "1rem",
                                  flexShrink: 0,
                                }} 
                              />
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontSize: "0.875rem",
                                  color: "#424242",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: "200px",
                                }}
                                title={prov.email}
                              >
                                {prov.email}
                              </Typography>
                            </Box>
                          )}
                          {prov.telefono && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <PhoneIcon 
                                fontSize="small" 
                                sx={{ 
                                  color: "#5b5b5f", 
                                  fontSize: "1rem",
                                  flexShrink: 0,
                                }} 
                              />
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontSize: "0.875rem",
                                  color: "#424242",
                                }}
                              >
                                {prov.telefono}
                              </Typography>
                            </Box>
                          )}
                          {!prov.email && !prov.telefono && (
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontSize: "0.875rem",
                                color: "#9e9e9e",
                                fontStyle: "italic",
                              }}
                            >
                              Sin contacto
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "6px 12px",
                          borderBottom: "1px solid #e0e0e0",
                        }}
                      >
                        <Tooltip
                          title={
                            prov.direccion || prov.localidad || prov.provincia
                              ? `${prov.direccion || ""} ${prov.localidad || ""} ${prov.provincia || ""}`.trim()
                              : "Sin ubicación"
                          }
                          arrow
                          placement="top"
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <LocationOnIcon 
                              fontSize="small" 
                              sx={{ 
                                color: "#5b5b5f", 
                                fontSize: "1rem",
                                flexShrink: 0,
                              }} 
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "0.875rem",
                                color: "#424242",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: "160px",
                              }}
                            >
                              {prov.localidad 
                                ? `${prov.localidad}${prov.provincia ? `, ${prov.provincia}` : ""}` 
                                : "—"}
                            </Typography>
                          </Box>
                        </Tooltip>
                      </TableCell>
                      {/* Celdas dinámicas de atributos */}
                      {entidadAtributoNoEliminados && entidadAtributoNoEliminados
                        .filter((attr) => !attr.eliminado)
                        .map((attr, index) => {
                          const opcionAtributo = prov[`opcionAtributo${index + 1}`];
                          const valorAtributo = opcionAtributo?.descripcion || null;
                          return (
                            <TableCell
                              key={attr.id}
                              sx={{
                                padding: "6px 12px",
                                borderBottom: "1px solid #e0e0e0",
                              }}
                            >
                              {valorAtributo ? (
                                <Chip
                                  label={valorAtributo}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    fontWeight: 500,
                                    fontSize: "0.75rem",
                                    height: 24,
                                    borderColor: "#5b5b5f",
                                    color: "#5b5b5f",
                                  }}
                                />
                              ) : (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: "0.875rem",
                                    color: "#9e9e9e",
                                    fontStyle: "italic",
                                  }}
                                >
                                  —
                                </Typography>
                              )}
                            </TableCell>
                          );
                        })}
                      <TableCell
                        align="center"
                        sx={{
                          padding: "6px 12px",
                          borderBottom: "1px solid #e0e0e0",
                        }}
                      >
                        <Tooltip title="Editar entidad" arrow>
                          <IconButton
                            onClick={() => handleOpenEditModal(prov)}
                            size="small"
                            sx={{
                              backgroundColor: "rgba(91, 91, 95, 0.08)",
                              color: "#5b5b5f",
                              padding: "6px",
                              "&:hover": {
                                backgroundColor: "rgba(91, 91, 95, 0.15)",
                                transform: "scale(1.05)",
                              },
                              transition: "all 0.2s ease",
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell 
                      colSpan={7 + (entidadAtributoNoEliminados?.filter((attr) => !attr.eliminado).length || 0)} 
                      align="center" 
                      sx={{ py: 6 }}
                    >
                      {loading ? (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                          <CircularProgress size={40} />
                          <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Cargando Clientes...
                          </Typography>
                        </Box>
                      ) : searchText || Object.values(atributosEntidadFiltro).some(v => v) ? (
                        <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                          No se encontraron Clientes con ese criterio de búsqueda
                        </Typography>
                      ) : (
                        <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                          No hay Clientes registrados
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Paginación */}
          <Box sx={{ flexShrink: 0, p: 1.5, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
            <CustomPagination
              currentPage={page}
              totalPages={totalPages}
              totalCount={totalCount}
              vieneDe="tablaEntidades"
              onPageChange={handleChangePage}
            />
          </Box>
        </Box>
      </Card>

      {/* Modal de Crear / Editar Cliente */}
      {openModal && (
        <ModalCrearActualizarEntidad
          open={openModal}
          handleClose={handleCloseModal}
          entidad={selectedProveedor}
          idTipoEntidad={1}
        />
      )}
    </Box>
  );
};

export default TablaEntidadProveedor;
